import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LevelForm from '../../../components/levels/LevelForm';
import { createLevel, updateLevel, getLevel } from '../../../api/levels';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeaderSection from '../../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../../components/layout/PageLoadingShell';
import { apiErrorMessage } from '../../../utils/apiError';

const AdminLevelFormPage = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const isEdit = !!id;
    const [initial, setInitial] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [loadError, setLoadError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setLoadError('');
            try {
                const res = await getLevel('ADMIN', id);
                if (res.data) setInitial(res.data);
                else setLoadError(t('forms.level.loadFailed', { defaultValue: 'Could not load this level.' }));
            } catch (e) {
                setLoadError(apiErrorMessage(e, t('forms.level.loadFailed', { defaultValue: 'Could not load this level.' })));
            } finally {
                setLoading(false);
            }
        };
        if (isEdit) load();
    }, [id, isEdit, t]);

    const onSubmit = async (payload) => {
        try {
            if (isEdit) await updateLevel(id, payload);
            else await createLevel(payload);
            navigate('/admin/levels');
        } catch (e) {
            alert(t('forms.level.saveFailed'));
        }
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={isEdit ? t('forms.level.editTitle') : t('forms.level.addTitle')}
                subtitle={t('forms.level.subtitle')}
            />
        );
    }

    if (isEdit && (loadError || !initial)) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    title={t('forms.level.editTitle')}
                    subtitle={t('forms.level.subtitle')}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {loadError || t('forms.level.loadFailed', { defaultValue: 'Could not load this level.' })}
                </div>
                <button type="button" className="mt-4 text-sm font-semibold text-slate-700 underline" onClick={() => navigate('/admin/levels')}>
                    {t('forms.common.back', { defaultValue: 'Back' })}
                </button>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={isEdit ? t('forms.level.editTitle') : t('forms.level.addTitle')}
                subtitle={t('forms.level.subtitle')}
            />
            <LevelForm
                role="ADMIN"
                initial={initial}
                onSubmit={onSubmit}
                onCancel={() => navigate('/admin/levels')}
                submitLabel={isEdit ? t('forms.common.update') : t('forms.common.create')}
            />
        </div>
    );
};

export default AdminLevelFormPage;
